'use client';

import { c as _c } from "react/compiler-runtime";
import { Fragment } from 'react';
import './EventLog.css';
import EventLogCard from '@/features/timeline/EventLogCard';
import SortToggle from '@/features/timeline/SortToggle';
import { useEventLogSort } from '@/features/timeline/useEventLogSort.mjs';
import { groupEventsByDay } from '@/features/timeline/groupEventsByDay.mjs';
import { countOutcomes } from '@/shared/utils/game/eventFilters.mjs';
import { eventKey } from '@/shared/utils/game/eventKey.mjs';
import { FACTION_SLUG_BY_ID } from '@/shared/enums/factions.mjs';

/**
 * Unified event log, shared between the homepage (`/`) and the archives
 * scrollytelling section (`/archives`). Groups events by day and renders
 * them as `EventLogCard`s. Does NOT render the vertical timeline rail —
 * that was removed; the archives page remains the place to go for
 * scrubbing a timeline visually.
 *
 * Props:
 * - `events`: full event list, unsorted
 * - `timeFormat`: `'live'` for homepage (ticking relative time) or
 *   `'absolute'` for archives (static absolute date/time)
 * - `title`: heading text (defaults to "Event Log")
 * - `id`: DOM id on the section wrapper, used by anchor-scroll
 * - `selectedEventKey` (optional): highlight the card matching this key
 * - `highlightedKeys` (optional): a `Set` of `eventKey` strings; matching
 *   cards get `data-highlighted` for the cascade deep-link faction tint
 * - `onHoverEvent` (optional): called with `(event)` on card hover
 * - `railRef` (optional): forwarded to the scrolling container so
 *   `useScrollEvent` on `/archives` can query `[data-event-key]` cards
 * - `layout` (optional, default `'grid'`): `'grid'` renders the
 *   desktop multi-column layout at ≥md. `'stack'` forces a single
 *   vertical column regardless of viewport width — required by the
 *   archives scrollytelling flow because `useScrollEvent`'s DOM-order
 *   optimization only works on a single column.
 * - `introMarkers` (optional, default `[]`): archives-only synthetic
 *   "faction enters the war" rows (`buildIntroMarkers`). They're
 *   interleaved chronologically with the event rows and rendered as a
 *   distinct faction-colored divider. The homepage passes nothing, so
 *   with the default empty array the output is byte-for-byte identical
 *   to before — the live dashboard is unaffected.
 * - `futureSlot` (optional, default `null`): homepage-only forecast content
 *   (the NextWaveCard) rendered as its own "FUTURE" day-group. Placed
 *   chronologically: before the day groups when sorting newest-first,
 *   after them when sorting oldest-first. Archives passes nothing.
 *
 * @param {object} props - Component props.
 * @param {Array} [props.events] - Full event list, unsorted.
 * @param {'live' | 'absolute'} [props.timeFormat] - Time display mode.
 * @param {string} [props.title] - Heading text.
 * @param {string} [props.id] - DOM id on the section wrapper.
 * @param {string} [props.initialSortOrder] - Initial sort preference (`'desc'` or `'asc'`).
 * @param {string | null} [props.selectedEventKey] - Highlight the card matching this key.
 * @param {Set<string> | null} [props.highlightedKeys] - Cascade deep-link highlight set; matching cards get `data-highlighted` for the faction tint.
 * @param {(event: object) => void} [props.onHoverEvent] - Called on card hover.
 * @param {object} [props.railRef] - Forwarded to the scrolling container.
 * @param {string} [props.layout] - Layout mode (`'grid'` or `'stack'`).
 * @param {Array<{kind:'intro', enemy:number, name:string, time:number, day:number, isWarStart:boolean}>} [props.introMarkers] - Intro markers (`buildIntroMarkers`).
 * @param {import('react').ReactNode} [props.futureSlot] - Forecast content rendered as a "FUTURE" day-group.
 */
export default function EventLog(t0) {
  const $ = _c(15);
  const {
    events,
    timeFormat,
    title: t1,
    id: t2,
    initialSortOrder,
    selectedEventKey: t3,
    highlightedKeys: t4,
    onHoverEvent,
    railRef,
    layout: t5,
    introMarkers: t6,
    futureSlot: t7
  } = t0;
  const title = t1 === undefined ? "Event Log" : t1;
  const id = t2 === undefined ? "event-log" : t2;
  const selectedEventKey = t3 === undefined ? null : t3;
  const highlightedKeys = t4 === undefined ? null : t4;
  const layout = t5 === undefined ? "grid" : t5;
  const introMarkers = t6 === undefined ? [] : t6;
  const futureSlot = t7 === undefined ? null : t7;
  const [sortOrder, toggleSortOrder] = useEventLogSort(initialSortOrder);
  const rows = introMarkers.length === 0 ? events ?? [] : [...(events ?? []), ...introMarkers.map(_temp)];
  const groups = groupEventsByDay(rows, {
    sortOrder
  });
  const t8 = "event-log-section";
  const t9 = "event-log-content";
  let t10;
  if ($[0] !== title) {
    t10 = <h2 className="event-log-heading">{title}</h2>;
    $[0] = title;
    $[1] = t10;
  } else {
    t10 = $[1];
  }
  const t11 = sortOrder === "desc";
  const t12 = sortOrder === "desc" ? "Sort oldest first" : "Sort newest first";
  let t13;
  if ($[2] !== t11 || $[3] !== t12 || $[4] !== toggleSortOrder) {
    t13 = <SortToggle descending={t11} onToggle={toggleSortOrder} label={t12} umamiEvent="event-log-sort-toggle" />;
    $[2] = t11;
    $[3] = t12;
    $[4] = toggleSortOrder;
    $[5] = t13;
  } else {
    t13 = $[5];
  }
  let t14;
  if ($[6] !== t10 || $[7] !== t13) {
    t14 = <div className="event-log-header">{t10}{t13}</div>;
    $[6] = t10;
    $[7] = t13;
    $[8] = t14;
  } else {
    t14 = $[8];
  }
  const t15 = groups.length === 0 && !futureSlot ? <p className="event-log-empty">No events recorded yet.</p> : <div className={layout === "stack" ? "event-log-days event-log-days--stack" : "event-log-days"} ref={railRef}>{futureSlot && sortOrder !== "asc" && <FutureGroup>{futureSlot}</FutureGroup>}{groups.map(group => {
      const {
        wins,
        losses
      } = countOutcomes(group.events.filter(_temp2));
      return <Fragment key={group.date}><div className="event-log-day"><div className="event-log-day-header"><span className="event-log-day-label">{group.label}</span>{(wins > 0 || losses > 0) && <span className="event-log-day-summary">{wins}W / {losses}L</span>}</div><div className="event-log-day-grid">{group.events.map(row_0 => {
              if (row_0.__intro) {
                return <IntroMarker key={`intro-${row_0.enemy}`} marker={row_0} />;
              }
              const event = row_0;
              const key = eventKey(event);
              return <div key={`${group.date}-${event.event_id}`} data-event-key={key} data-faction={String(event.enemy)} data-highlighted={highlightedKeys?.has(key) ? "" : undefined}><EventLogCard event={event} timeFormat={timeFormat} isSelected={key === selectedEventKey} onMouseEnter={onHoverEvent ? () => onHoverEvent(event) : undefined} /></div>;
            })}</div></div></Fragment>;
    })}{futureSlot && sortOrder === "asc" && <FutureGroup>{futureSlot}</FutureGroup>}</div>;
  let t16;
  if ($[9] !== t14 || $[10] !== t15) {
    t16 = <div className={t9}>{t14}{t15}</div>;
    $[9] = t14;
    $[10] = t15;
    $[11] = t16;
  } else {
    t16 = $[11];
  }
  let t17;
  if ($[12] !== id || $[13] !== t16) {
    t17 = <section id={id} className={t8}>{t16}</section>;
    $[12] = id;
    $[13] = t16;
    $[14] = t17;
  } else {
    t17 = $[14];
  }
  return t17;
}

/**
 * The forecast's day-group: same skeleton as a date group, labeled FUTURE so
 * the log reads chronologically (future → today → yesterday when sorting
 * newest-first). No win/loss summary — nothing has happened yet.
 *
 * @param {object} props - Component props.
 * @param {import('react').ReactNode} props.children - Forecast content.
 */
function _temp2(row) {
  return !row.__intro;
}
function _temp(m) {
  return {
    ...m,
    __intro: true,
    start_time: m.time
  };
}
function FutureGroup(t0) {
  const $ = _c(3);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="event-log-day-header"><span className="event-log-day-label">FUTURE</span></div>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children) {
    t2 = <div className="event-log-day">{t1}<div className="event-log-day-grid">{children}</div></div>;
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}

/**
 * A synthetic "faction enters the war" divider, interleaved among the event
 * rows on both the homepage and /archives. Faction-colored via the
 * `--color-faction-*` theme tokens (resolved from the enemy id's slug). The
 * war-start faction (`isWarStart`, HD1 introduction_order 0) gets distinct
 * wording — it opens the war rather than joining an ongoing one. Static — no
 * interactivity, so no umami tracking is needed.
 *
 * @param {object} props - Component props.
 * @param {{enemy:number, name:string, day:number, isWarStart:boolean}} props.marker - One `buildIntroMarkers` entry.
 */
function IntroMarker(t0) {
  const $ = _c(11);
  const {
    marker
  } = t0;
  const slug = FACTION_SLUG_BY_ID[marker.enemy] ?? "bugs";
  const t1 = `var(--color-faction-${slug})`;
  let t2;
  if ($[0] !== t1) {
    t2 = {
      "--intro-color": t1
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== marker.day) {
    t3 = <span className="event-log-intro-day">Day {marker.day}</span>;
    $[2] = marker.day;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = marker.isWarStart ? "declare war" : "enter the war";
  let t5;
  if ($[4] !== marker.name || $[5] !== t4) {
    t5 = <span className="event-log-intro-label">{marker.name} {t4}</span>;
    $[4] = marker.name;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t2 || $[8] !== t3 || $[9] !== t5) {
    t6 = <div className="event-log-intro" style={t2}>{t3}{t5}</div>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
}